// apps/client/src/api/team.ts
import {
  UserWithRole,
  UserRole,
  ApiResponse,
  OrganisationTeamResponse,
  OrganisationTeamUserAddResponse,
  OrganisationTeamUserDeleteResponse,
} from '@fsp/types';
import { apiClient } from './apiClient';

// Helper function for consistent response handling
const handleResponse = async (response: Response): Promise<ApiResponse> => {
  const result: ApiResponse = await response.json();

  if (!response.ok || !result.success) {
    throw new Error(result.message || `Request failed: ${response.statusText}`);
  }

  return result;
};

export const fetchTeamMembers = async (organisationId: string): Promise<UserWithRole[]> => {
  const response = await apiClient.get(`/organisations/${organisationId}/team`);

  if (!response.ok) {
    throw new Error(`Failed to fetch team members: ${response.statusText}`);
  }

  const result: OrganisationTeamResponse = await response.json();
  return result.users || [];
};

export const updateMemberRole = async (
  organisationId: string,
  memberId: string,
  role: UserRole
): Promise<UserWithRole> => {
  const response = await apiClient.put(`/organisations/${organisationId}/team/${memberId}`, {
    body: JSON.stringify({ role }),
  });

  if (!response.ok) {
    const errorResult: ApiResponse = await response.json();
    throw new Error(errorResult.message || `Failed to update member role: ${response.statusText}`);
  }

  const result: OrganisationTeamUserAddResponse = await response.json();
  return result.user;
};

export const deleteMember = async (
  organisationId: string,
  memberId: string
): Promise<{ userId: string }> => {
  const response = await apiClient.delete(`/organisations/${organisationId}/team/${memberId}`);

  if (!response.ok) {
    throw new Error(`Failed to delete team members: ${response.statusText}`);
  }

  const result: OrganisationTeamUserDeleteResponse = await response.json();
  return { userId: result.userId };
};

export const leaveOrganisation = async (organisationId: string): Promise<ApiResponse> => {
  const response = await apiClient.delete(`/user/organisations/${organisationId}`);
  return await handleResponse(response);
};
